import { useEffect, useRef, type ReactNode } from "react";
import { useIntl } from "react-intl";
import { Button } from "../design/Button";
import type { DialogDismiss } from "../design/Modal";

// Native modal dialog with built-in backdrop, focus trap, Escape key. Closes by the same
// `dismiss` contract as <Modal> — see DialogDismiss there.

interface DialogProps {
  open: boolean;
  onClose: () => void;
  /** Defaults to `light` — see Modal. Any dialog that holds a draft or asks a question must say `explicit`. */
  dismiss?: DialogDismiss;
  /** `explicit` only: the draft differs from what was opened, so Escape no longer closes. */
  dirty?: boolean;
  title?: ReactNode;
  children: ReactNode;
  /** Maximum width - defaults to a comfortable sm. */
  maxWidth?: "sm" | "md" | "lg" | "xl";
  /** An operation is in flight: nothing closes the dialog, and the × is disabled. */
  preventClose?: boolean;
  /** Testanker auf dem <dialog> (UI_CONVENTIONS → Test anchors). */
  id?: string;
  /** Testanker des ×; ohne Angabe `${id}-close`. */
  closeId?: string;
  "data-testid"?: string;
}

const MAX_WIDTHS: Record<NonNullable<DialogProps["maxWidth"]>, string> = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-6xl",
};

export function Dialog({ open, onClose, dismiss = "light", dirty = false, title, children, maxWidth = "sm", preventClose = false, id, closeId = id ? `${id}-close` : undefined, "data-testid": testId }: DialogProps) {
  const intl = useIntl();
  const ref = useRef<HTMLDialogElement>(null);
  const light = dismiss === "light";
  const escCloses = !preventClose && (light || !dirty);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open]);

  // Sync React state with native dialog close/cancel events
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = () => onClose();
    el.addEventListener("close", handler);
    return () => el.removeEventListener("close", handler);
  }, [onClose]);

  // Suppress native Escape while it must not close. The dialog's `cancel` event is
  // cancelable; preventDefault stops the browser from closing it (and thus from firing
  // `close`), so React state and the native dialog stay in sync.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onCancel = (e: Event) => {
      if (!escCloses) e.preventDefault();
    };
    el.addEventListener("cancel", onCancel);
    return () => el.removeEventListener("cancel", onCancel);
  }, [escCloses]);

  const closeButton = light ? (
    <Button
      id={closeId}
      variant="icon"
      size="sm"
      className="absolute right-3 top-5"
      aria-label={intl.formatMessage({ id: "common.close", defaultMessage: "Close" })}
      disabled={preventClose}
      onClick={onClose}
    >
      ✕
    </Button>
  ) : null;

  return (
    <dialog
      ref={ref}
      id={id}
      // Chromium (CloseWatcher) stops honouring a prevented `cancel` after the first Escape
      // without user activation in between; preventing the keydown never fires `cancel` at all.
      onKeyDown={(e) => {
        if (e.key === "Escape" && !escCloses) e.preventDefault();
      }}
      onClick={(e) => {
        // Backdrop click (target is the dialog itself, not content) — light dialogs only.
        if (e.target === ref.current && light && !preventClose) onClose();
      }}
      className={`fixed inset-0 m-auto h-fit max-h-[calc(100vh-2rem)] ${MAX_WIDTHS[maxWidth]} w-full rounded border border-ink/5 bg-surface p-0 shadow-xl backdrop:bg-ink/40 backdrop:backdrop-blur-md`}
      data-testid={testId}
    >
      <div className="relative p-6">
        {title ? (
          <h2 id={id ? `${id}-heading` : undefined} className={`mb-4 text-smycles-title font-semibold ${closeButton ? "pr-8" : ""}`}>{title}</h2>
        ) : null}
        {children}
        {/* Last in the DOM, so showModal() still focuses the content's first control, not the ×. */}
        {closeButton}
      </div>
    </dialog>
  );
}
